import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppShell from '@/components/feature/AppShell';
import DateNavigator from '@/pages/dashboard/admin/attendance/components/DateNavigator';
import AttendanceStatsBar from '@/pages/dashboard/admin/attendance/components/AttendanceStatsBar';
import AttendanceTable from '@/pages/dashboard/admin/attendance/components/AttendanceTable';
import PreInspectionModal from '@/pages/dashboard/admin/attendance/components/PreInspectionModal';
import IncidentModal from '@/pages/dashboard/admin/attendance/components/IncidentModal';
import PreInspectionLogList from '@/pages/dashboard/admin/attendance/components/PreInspectionLogList';
import { ConfirmDialog } from '@/components/base/Modal';
import { attendanceRecords, type AttendanceRecord } from '@/mocks/attendance';
import { students } from '@/mocks/students';
import { teacherNotifications } from '@/mocks/dashboard';
import { preInspectionLogs as initialPiLogs, incidentLogs as initialIncidentLogs, type PreInspectionLog, type IncidentLog } from '@/mocks/preInspection';

const TEACHER_NAME = 'Mr. James Okafor';
const TEACHER_CLASS = 'Primary - B';

interface ModalState {
  type: 'preInspection' | 'incident' | null;
  studentId: string;
  studentName: string;
  className: string;
}

export default function TeacherAttendancePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const todayStr = new Date().toISOString().split('T')[0];
  const [selectedDate, setSelectedDate] = useState<string>(todayStr);
  const [localRecords, setLocalRecords] = useState<AttendanceRecord[]>(() => [...attendanceRecords]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNotifyConfirm, setShowNotifyConfirm] = useState(false);
  const [notifySent, setNotifySent] = useState(false);

  const [modalState, setModalState] = useState<ModalState>({
    type: null,
    studentId: '',
    studentName: '',
    className: '',
  });
  const [showLogsModal, setShowLogsModal] = useState(false);

  const [piLogs, setPiLogs] = useState<PreInspectionLog[]>(() => [...initialPiLogs]);
  const [incidentLogs, setIncidentLogs] = useState<IncidentLog[]>(() => [...initialIncidentLogs]);

  const baseRecords = useMemo(() => {
    let records = localRecords.filter(
      (r) => r.date === selectedDate && r.classTeacher === TEACHER_NAME,
    );

    if (records.length === 0 && selectedDate === todayStr) {
      const enrolledStudents = students.filter(
        (s) => s.classTeacher === TEACHER_NAME && (s.status === 'enrolled' || s.status === 'pending'),
      );

      records = enrolledStudents.map((s) => ({
        id: `att-new-${s.id}-${selectedDate}`,
        studentId: s.studentId,
        studentName: s.name,
        className: s.className,
        classTeacher: s.classTeacher,
        date: selectedDate,
        status: 'present' as const,
        remark: '',
      }));
    }

    return records;
  }, [localRecords, selectedDate]);

  const attendanceStats = useMemo(() => {
    const presentCount = baseRecords.filter((r) => r.status === 'present').length;
    const total = baseRecords.length;

    const classGroups: Record<string, { present: number; total: number }> = {};
    for (const r of baseRecords) {
      if (!classGroups[r.className]) classGroups[r.className] = { present: 0, total: 0 };
      classGroups[r.className].total++;
      if (r.status === 'present') classGroups[r.className].present++;
    }

    let bestClassName: string | null = null;
    let bestClassRate = 0;
    let worstClassName: string | null = null;
    let worstClassRate = 100;

    for (const [cls, data] of Object.entries(classGroups)) {
      const rate = Math.round((data.present / data.total) * 100);
      if (rate > bestClassRate || (rate === bestClassRate && bestClassName === null)) {
        bestClassRate = rate;
        bestClassName = cls;
      }
      if (rate < worstClassRate || (rate === worstClassRate && worstClassName === null)) {
        worstClassRate = rate;
        worstClassName = cls;
      }
    }

    if (bestClassName === worstClassName) {
      worstClassName = null;
      worstClassRate = 0;
    }

    return { presentCount, total, bestClassName, bestClassRate, worstClassName, worstClassRate };
  }, [baseRecords]);

  const absentStudents = useMemo(
    () => baseRecords.filter((r) => r.status === 'absent'),
    [baseRecords],
  );
  const isToday = selectedDate === todayStr;
  const showNotifyButton = isToday && absentStudents.length > 0;

  const handleNotifyParents = useCallback(() => {
    setShowNotifyConfirm(false);
    setNotifySent(true);
  }, []);

  const handleToggle = useCallback((recordId: string) => {
    setLocalRecords((prev) =>
      prev.map((r) =>
        r.id === recordId
          ? { ...r, status: r.status === 'present' ? ('absent' as const) : ('present' as const) }
          : r,
      ),
    );
  }, []);

  const handleRemarkChange = useCallback((recordId: string, remark: string) => {
    setLocalRecords((prev) =>
      prev.map((r) => (r.id === recordId ? { ...r, remark } : r)),
    );
  }, []);

  const handleMarkAll = useCallback(
    (status: 'present' | 'absent') => {
      const targetIds = new Set(baseRecords.map((r) => r.id));
      setLocalRecords((prev) =>
        prev.map((r) => (targetIds.has(r.id) ? { ...r, status } : r)),
      );
    },
    [baseRecords],
  );

  const openPreInspection = useCallback((studentId: string, studentName: string, className: string) => {
    setModalState({ type: 'preInspection', studentId, studentName, className });
  }, []);

  const openIncident = useCallback((studentId: string, studentName: string, className: string) => {
    setModalState({ type: 'incident', studentId, studentName, className });
  }, []);

  const closeModal = useCallback(() => {
    setModalState({ type: null, studentId: '', studentName: '', className: '' });
  }, []);

  const handleSavePreInspection = useCallback((log: PreInspectionLog) => {
    setPiLogs((prev) => [...prev, log]);
  }, []);

  const handleSaveIncident = useCallback((log: IncidentLog) => {
    setIncidentLogs((prev) => [...prev, log]);
  }, []);

  const todayPiCount = piLogs.filter((l) => l.date === selectedDate).length;
  const todayIncidentCount = incidentLogs.filter((l) => l.date === selectedDate).length;
  const hasAnyLogs = todayPiCount > 0 || todayIncidentCount > 0;

  return (
    <AppShell
      pageTitle={t('attendance.teacherAttendanceTitle', { defaultValue: 'Class Attendance' })}
      pageSubtitle={`${TEACHER_CLASS} — ${TEACHER_NAME}`}
      breadcrumbs={[
        { label: t('common.home') },
        { label: t('common.dashboard'), path: '/dashboard/teacher-dashboard' },
        { label: t('attendance.studentAttendance') },
      ]}
      notifications={teacherNotifications}
    >
      {/* Date Navigator */}
      <div className="mb-5">
        <DateNavigator
          selectedDate={selectedDate}
          onDateChange={setSelectedDate}
          viewerName={TEACHER_NAME}
          viewerRole="Teacher"
        />
      </div>

      {/* Filters Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 mb-4">
        <div className="relative flex-1 w-full sm:max-w-sm">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name or ID..."
            className="w-full pl-9 pr-9 py-2.5 sm:py-2 text-sm font-label rounded-lg border border-background-200 bg-background-50 text-foreground-700 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-secondary-300 focus:border-secondary-400"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 cursor-pointer"
            >
              <i className="ri-close-circle-line text-sm" />
            </button>
          )}
        </div>

        <button
          onClick={() => setShowLogsModal(true)}
          className={`flex items-center gap-1.5 px-3 py-2 text-xs font-label font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
            hasAnyLogs
              ? 'bg-accent-100 text-accent-800 border border-accent-200 hover:bg-accent-200'
              : 'bg-background-50 text-foreground-500 border border-background-200 hover:bg-background-100'
          }`}
        >
          <i className="ri-file-list-3-line text-sm" />
          {t('attendance.viewAllPreInspectionLogs')}
          {hasAnyLogs && (
            <span className="px-1.5 py-0.5 text-[10px] rounded-full bg-accent-200 text-accent-700">
              {todayPiCount + todayIncidentCount}
            </span>
          )}
        </button>

        <button
          onClick={() => navigate('/dashboard/teacher-dashboard')}
          className="flex items-center gap-1.5 px-3 py-2 text-xs font-label font-medium text-foreground-500 hover:text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer ml-auto whitespace-nowrap"
        >
          <i className="ri-arrow-left-line text-sm" />
          Back to Dashboard
        </button>
      </div>

      {/* Attendance Stats Bar */}
      <AttendanceStatsBar
        totalStudents={attendanceStats.total}
        presentCount={attendanceStats.presentCount}
        absentCount={attendanceStats.total - attendanceStats.presentCount}
        bestClassName={attendanceStats.bestClassName}
        bestClassRate={attendanceStats.bestClassRate}
        worstClassName={attendanceStats.worstClassName}
        worstClassRate={attendanceStats.worstClassRate}
      />

      {/* Notify Parents — success banner */}
      {notifySent && (
        <div className="mb-5 p-4 rounded-xl bg-semantic-success-bg border border-semantic-success/30 flex items-start gap-3">
          <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-semantic-success/15 flex-shrink-0">
            <i className="ri-mail-check-line text-semantic-success text-lg" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-label font-700 text-foreground-800">
              {t('attendance.parentsNotified')}
            </p>
            <p className="text-xs text-foreground-600 font-label mt-0.5">
              Absence alerts sent for{' '}
              <span className="font-700">{absentStudents.length} student{absentStudents.length !== 1 ? 's' : ''}</span>
              {' '}— {absentStudents.map((s) => s.studentName).slice(0, 3).join(', ')}
              {absentStudents.length > 3 ? ` and ${absentStudents.length - 3} more` : ''}.
            </p>
          </div>
          <button
            onClick={() => setNotifySent(false)}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-foreground-400 hover:text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-sm" />
          </button>
        </div>
      )}

      {/* Notify Parents — button row */}
      {showNotifyButton && !notifySent && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4">
          <button
            onClick={() => setShowNotifyConfirm(true)}
            className="flex items-center justify-center gap-2 w-full sm:w-auto px-4 py-2.5 sm:py-2 text-sm font-label font-medium rounded-lg bg-accent-100 text-accent-800 border border-accent-200 hover:bg-accent-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-mail-send-line text-base" />
            Notify {absentStudents.length} Parent{absentStudents.length !== 1 ? 's' : ''}
          </button>
          <span className="text-xs text-foreground-400 font-label text-center sm:text-left">
            {absentStudents.length} student{absentStudents.length !== 1 ? 's' : ''} marked absent today
          </span>
        </div>
      )}

      {/* Notify Parents — confirmation dialog */}
      <ConfirmDialog
        open={showNotifyConfirm}
        onClose={() => setShowNotifyConfirm(false)}
        onConfirm={handleNotifyParents}
        title="Send Absence Alerts?"
        message={`This will notify parents of ${absentStudents.length} absent student${absentStudents.length !== 1 ? 's' : ''} (${absentStudents.map((s) => s.studentName).slice(0, 5).join(', ')}${absentStudents.length > 5 ? ` and ${absentStudents.length - 5} more` : ''}). Parents will receive an email with today's absence details.`}
        confirmLabel={`Notify ${absentStudents.length} Parent${absentStudents.length !== 1 ? 's' : ''}`}
        cancelLabel="Cancel"
      />

      {/* Attendance Table */}
      <AttendanceTable
        records={baseRecords}
        selectedDate={selectedDate}
        groupedByClass={false}
        onToggle={handleToggle}
        onRemarkChange={handleRemarkChange}
        onMarkAll={handleMarkAll}
        searchQuery={searchQuery}
        classFilter=""
        onPreInspection={openPreInspection}
        onIncident={openIncident}
        incidentLogs={incidentLogs}
      />

      {/* Pre-Inspection Modal */}
      <PreInspectionModal
        open={modalState.type === 'preInspection'}
        onClose={closeModal}
        studentId={modalState.studentId}
        studentName={modalState.studentName}
        className={modalState.className}
        onSave={handleSavePreInspection}
        existingLogs={piLogs}
        userRole="Teacher"
        userName={TEACHER_NAME}
      />

      {/* Incident Modal */}
      <IncidentModal
        open={modalState.type === 'incident'}
        onClose={closeModal}
        studentId={modalState.studentId}
        studentName={modalState.studentName}
        className={modalState.className}
        onSave={handleSaveIncident}
        existingLogs={incidentLogs}
        userRole="Teacher"
        userName={TEACHER_NAME}
      />

      {/* Daywise Logs Modal */}
      <PreInspectionLogList
        open={showLogsModal}
        onClose={() => setShowLogsModal(false)}
        selectedDate={selectedDate}
        preInspectionLogs={piLogs}
        incidentLogs={incidentLogs}
      />
    </AppShell>
  );
}